/**
 * Utilities for calculating open/closed status from cached opening hours data.
 * This allows us to cache the schedule for 30 days while still showing
 * accurate real-time open/closed status (calculated client-side from the schedule).
 */

// Cache TTL constants (in days)
export const OPENING_HOURS_CACHE_DAYS = 30;
export const PHOTO_URL_CACHE_DAYS = 30;

/**
 * Check if cached opening hours data is stale
 * @param {string|Date} savedAt - When the data was saved
 * @param {number} maxAgeDays - Maximum age in days (default 30)
 * @returns {boolean} - True if stale and needs refresh
 */
export const isOpeningHoursStale = (savedAt, maxAgeDays = OPENING_HOURS_CACHE_DAYS) => {
  if (!savedAt) return true;

  const savedDate = new Date(savedAt);
  const now = new Date();
  const daysSinceSaved = (now - savedDate) / (1000 * 60 * 60 * 24);

  return daysSinceSaved > maxAgeDays;
};

/**
 * Check if cached photo URL is stale
 * @param {string|Date} savedAt - When the data was saved
 * @param {number} maxAgeDays - Maximum age in days (default 30)
 * @returns {boolean} - True if stale and needs refresh
 */
export const isPhotoUrlStale = (savedAt, maxAgeDays = PHOTO_URL_CACHE_DAYS) => {
  if (!savedAt) return true;

  const savedDate = new Date(savedAt);
  const now = new Date();
  const daysSinceSaved = (now - savedDate) / (1000 * 60 * 60 * 24);

  return daysSinceSaved > maxAgeDays;
};

/**
 * Helper to extract time value from a period's open/close object.
 * Handles both formats:
 * - Old format: { time: "0800" } (string HHMM)
 * - New format: { hours: 8, minutes: 0 } (separate fields from Places API New)
 * @returns {number} Time as HHMM integer (e.g., 800 for 8:00 AM, 1600 for 4:00 PM)
 */
const getTimeValue = (timeObj, defaultValue = 0) => {
  if (!timeObj) return defaultValue;

  // New format: hours and minutes as separate fields
  if (timeObj.hours !== undefined) {
    return timeObj.hours * 100 + (timeObj.minutes || 0);
  }

  // Old format: time as string "HHMM"
  if (timeObj.time !== undefined) {
    return parseInt(timeObj.time, 10) || defaultValue;
  }

  return defaultValue;
};

/**
 * Helper to get time string from a period's open/close object.
 * Handles both formats and returns HHMM string.
 */
const getTimeString = (timeObj, defaultValue = '0000') => {
  if (!timeObj) return defaultValue;

  // New format: hours and minutes as separate fields
  if (timeObj.hours !== undefined) {
    const hours = String(timeObj.hours).padStart(2, '0');
    const minutes = String(timeObj.minutes || 0).padStart(2, '0');
    return hours + minutes;
  }

  // Old format: time as string
  return timeObj.time || defaultValue;
};

/**
 * Check if a period represents 24/7 operation
 */
const is24HoursPeriod = (period) => {
  if (!period.close) {
    const openTime = getTimeValue(period.open);
    return openTime === 0;
  }
  return false;
};

/**
 * Calculate if a place is currently open based on cached opening hours
 * @param {Object} openingHours - The opening_hours object from Google Places API
 * @returns {boolean|null} - true if open, false if closed, null if unknown
 */
export const calculateIsOpenNow = (openingHours) => {
  if (!openingHours?.periods) {
    return null;
  }

  // Check for 24/7 places first - they have a single period with no close time
  // This period opens on day 0 (Sunday) at 0000 and has no close property
  if (openingHours.periods.length === 1) {
    const period = openingHours.periods[0];
    if (is24HoursPeriod(period)) {
      return true; // Open 24/7
    }
  }

  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeValue = currentHour * 100 + currentMinute; // e.g., 1430 for 2:30 PM

  // Find today's periods
  const todayPeriods = openingHours.periods.filter(p => p.open?.day === currentDay);

  // Check if any period covers the current time
  for (const period of todayPeriods) {
    const openTime = getTimeValue(period.open, 0);

    // Handle 24-hour places (no close time means open 24 hours)
    if (!period.close) {
      return true;
    }

    const closeDay = period.close.day;
    const closeTime = getTimeValue(period.close, 2359);

    // Same day closing
    if (closeDay === currentDay) {
      if (currentTimeValue >= openTime && currentTimeValue < closeTime) {
        return true;
      }
    }
    // Closes next day (e.g., bar open until 2am)
    else if (closeDay === (currentDay + 1) % 7) {
      // If we're after opening time today, we're open
      if (currentTimeValue >= openTime) {
        return true;
      }
    }
  }

  // Check if we're in a period that opened yesterday and closes today
  const yesterdayDay = (currentDay + 6) % 7;
  const yesterdayPeriods = openingHours.periods.filter(p => p.open?.day === yesterdayDay);

  for (const period of yesterdayPeriods) {
    if (period.close?.day === currentDay) {
      const closeTime = getTimeValue(period.close, 0);
      if (currentTimeValue < closeTime) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Get detailed open status information for display
 * @param {Object} openingHours - The opening_hours object from Google Places API
 * @param {string} businessStatus - The business_status from Google Places API (OPERATIONAL, CLOSED_TEMPORARILY, CLOSED_PERMANENTLY)
 * @returns {Object|null} - Status object with color, status, tooltip, or null if unknown
 */
export const getOpenStatusInfo = (openingHours, businessStatus) => {
  // Handle permanently closed businesses
  if (businessStatus === 'CLOSED_PERMANENTLY') {
    return {
      color: 'red',
      status: 'permanently-closed',
      tooltip: 'Permanently closed',
      isOpen: false,
      isPermanentlyClosed: true,
    };
  }

  // Handle temporarily closed businesses
  if (businessStatus === 'CLOSED_TEMPORARILY') {
    return {
      color: 'red',
      status: 'closed',
      tooltip: 'Temporarily closed',
      isOpen: false,
      isTemporarilyClosed: true,
    };
  }

  if (!openingHours?.periods) {
    return null;
  }

  // Check for 24/7 places first - they have a single period with no close time
  if (openingHours.periods.length === 1) {
    const period = openingHours.periods[0];
    if (is24HoursPeriod(period)) {
      return {
        color: 'green',
        status: 'open',
        tooltip: 'Open 24 hours',
        isOpen: true,
        is24Hours: true,
      };
    }
  }

  const isOpen = calculateIsOpenNow(openingHours);
  if (isOpen === null) {
    return null;
  }

  const now = new Date();
  const currentDay = now.getDay();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeValue = currentHour * 100 + currentMinute;

  if (isOpen) {
    // Find closing time
    const closeInfo = findNextCloseTime(openingHours, currentDay, currentTimeValue);

    if (closeInfo) {
      const minutesUntilClose = calculateMinutesUntil(currentTimeValue, closeInfo.time, closeInfo.isNextDay);
      const formattedCloseTime = formatTime(closeInfo.time);

      if (minutesUntilClose <= 60) {
        return {
          color: 'yellow',
          status: 'closing-soon',
          tooltip: `Closing soon at ${formattedCloseTime}`,
          isOpen: true,
          closesAt: formattedCloseTime,
          minutesUntilClose,
        };
      }

      return {
        color: 'green',
        status: 'open',
        tooltip: `Open \u2022 Closes at ${formattedCloseTime}`,
        isOpen: true,
        closesAt: formattedCloseTime,
      };
    }

    return {
      color: 'green',
      status: 'open',
      tooltip: 'Open',
      isOpen: true,
    };
  }

  // Closed - find next opening time
  const openInfo = findNextOpenTime(openingHours, currentDay, currentTimeValue);

  if (openInfo) {
    const formattedOpenTime = formatTime(openInfo.time);
    const dayLabel = getDayLabel(openInfo.day, currentDay);

    return {
      color: 'red',
      status: 'closed',
      tooltip: `Closed \u2022 Opens ${dayLabel} at ${formattedOpenTime}`,
      isOpen: false,
      opensAt: formattedOpenTime,
      opensDay: dayLabel,
    };
  }

  return {
    color: 'red',
    status: 'closed',
    tooltip: 'Closed',
    isOpen: false,
  };
};

/**
 * Find the next closing time from current time
 */
function findNextCloseTime(openingHours, currentDay, currentTimeValue) {
  // Check today's periods first
  const todayPeriods = openingHours.periods.filter(p => p.open?.day === currentDay);

  for (const period of todayPeriods) {
    const openTime = getTimeValue(period.open, 0);

    if (!period.close) {
      return null; // 24 hours
    }

    const closeDay = period.close.day;
    const closeTime = getTimeValue(period.close, 2359);
    const closeTimeStr = getTimeString(period.close);

    if (closeDay === currentDay && currentTimeValue >= openTime && currentTimeValue < closeTime) {
      return { time: closeTimeStr, isNextDay: false };
    }

    if (closeDay === (currentDay + 1) % 7 && currentTimeValue >= openTime) {
      return { time: closeTimeStr, isNextDay: true };
    }
  }

  // Check yesterday's period that might close today
  const yesterdayDay = (currentDay + 6) % 7;
  const yesterdayPeriods = openingHours.periods.filter(p => p.open?.day === yesterdayDay);

  for (const period of yesterdayPeriods) {
    if (period.close?.day === currentDay) {
      const closeTime = getTimeValue(period.close, 0);
      if (currentTimeValue < closeTime) {
        return { time: getTimeString(period.close), isNextDay: false };
      }
    }
  }

  return null;
}

/**
 * Find the next opening time from current time
 */
function findNextOpenTime(openingHours, currentDay, currentTimeValue) {
  // Check remaining today
  const todayPeriods = openingHours.periods.filter(p => p.open?.day === currentDay);
  for (const period of todayPeriods) {
    const openTime = getTimeValue(period.open, 0);
    if (openTime > currentTimeValue) {
      return { day: currentDay, time: getTimeString(period.open) };
    }
  }

  // Check upcoming days
  for (let i = 1; i <= 7; i++) {
    const checkDay = (currentDay + i) % 7;
    const dayPeriods = openingHours.periods.filter(p => p.open?.day === checkDay);

    if (dayPeriods.length > 0) {
      // Get earliest opening time for that day
      const earliestPeriod = dayPeriods.reduce((earliest, period) => {
        const time = getTimeValue(period.open, 2359);
        const earliestTime = getTimeValue(earliest.open, 2359);
        return time < earliestTime ? period : earliest;
      });

      return { day: checkDay, time: getTimeString(earliestPeriod.open) };
    }
  }

  return null;
}

/**
 * Calculate minutes until a target time
 */
function calculateMinutesUntil(currentTimeValue, targetTimeStr, isNextDay) {
  const currentMinutes = Math.floor(currentTimeValue / 100) * 60 + (currentTimeValue % 100);
  const targetTime = parseInt(targetTimeStr, 10);
  let targetMinutes = Math.floor(targetTime / 100) * 60 + (targetTime % 100);

  if (isNextDay) {
    targetMinutes += 24 * 60;
  }

  return targetMinutes - currentMinutes;
}

/**
 * Format time from HHMM to readable format
 */
function formatTime(timeStr) {
  const time = parseInt(timeStr, 10);
  const hour = Math.floor(time / 100);
  const minute = time % 100;
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  if (minute === 0) {
    return `${displayHour} ${period}`;
  }
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
}

/**
 * Get day label relative to current day
 */
function getDayLabel(targetDay, currentDay) {
  if (targetDay === currentDay) {
    return 'today';
  }
  if (targetDay === (currentDay + 1) % 7) {
    return 'tomorrow';
  }

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[targetDay];
}

/**
 * Time slot definitions for "Open At" filtering
 * Each slot has start/end times in HHMM format
 */
export const TIME_SLOTS = {
  morning: { label: 'Morning', start: 600, end: 1200 },      // 6am - 12pm
  afternoon: { label: 'Afternoon', start: 1200, end: 1700 }, // 12pm - 5pm
  evening: { label: 'Evening', start: 1700, end: 2100 },     // 5pm - 9pm
  lateNight: { label: 'Late Night', start: 2100, end: 200 }, // 9pm - 2am (crosses midnight)
};

/**
 * Day labels for display
 */
export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DAY_LABELS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Check if a place is open during a specific time slot on a given day
 *
 * Rule: The date is based on opening time. A place open 10pm-1am Tuesday into
 * Wednesday counts as "Tuesday Late Night" (not Wednesday).
 *
 * @param {Object} openingHours - The opening_hours object from Google Places API
 * @param {number} day - Day of week (0=Sunday, 6=Saturday)
 * @param {string} slotKey - Key from TIME_SLOTS ('morning', 'afternoon', 'evening', 'lateNight')
 * @returns {boolean|null} - true if open during that slot, false if closed, null if unknown
 */
export const isOpenDuring = (openingHours, day, slotKey) => {
  if (!openingHours?.periods) {
    return null;
  }

  const slot = TIME_SLOTS[slotKey];
  if (!slot) {
    return null;
  }

  // Check for 24/7 places
  if (openingHours.periods.length === 1) {
    const period = openingHours.periods[0];
    if (!period.close && period.open?.day === 0 && getTimeValue(period.open, 0) === 0) {
      return true; // Open 24/7
    }
  }

  // Find periods that open on the target day
  const periodsOnDay = openingHours.periods.filter(p => p.open?.day === day);

  for (const period of periodsOnDay) {
    const openTime = getTimeValue(period.open, 0);

    // Handle 24-hour places (no close time)
    if (!period.close) {
      // Check if opening time falls within our slot
      if (isTimeInSlot(openTime, slot)) {
        return true;
      }
      continue;
    }

    const closeDay = period.close.day;
    const closeTime = getTimeValue(period.close, 2359);

    // Check if this period overlaps with the time slot
    if (periodOverlapsSlot(openTime, closeTime, closeDay === day, slot)) {
      return true;
    }
  }

  // For non-late-night slots, also check if a period from yesterday extends into today's slot
  if (slotKey !== 'lateNight') {
    const yesterdayDay = (day + 6) % 7;
    const periodsFromYesterday = openingHours.periods.filter(p => p.open?.day === yesterdayDay);

    for (const period of periodsFromYesterday) {
      if (period.close?.day === day) {
        const closeTime = getTimeValue(period.close, 0);
        // The period extends into today - check if it overlaps with our slot
        if (closeTime > slot.start) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * Check if a time value falls within a slot
 */
function isTimeInSlot(time, slot) {
  if (slot.start < slot.end) {
    // Normal slot (e.g., morning: 600-1200)
    return time >= slot.start && time < slot.end;
  } else {
    // Overnight slot (e.g., lateNight: 2100-200)
    return time >= slot.start || time < slot.end;
  }
}

/**
 * Check if a period (open to close) overlaps with a time slot
 */
function periodOverlapsSlot(openTime, closeTime, sameDay, slot) {
  if (slot.start < slot.end) {
    // Normal slot
    if (sameDay) {
      // Period opens and closes same day
      // Overlap if: openTime < slot.end AND closeTime > slot.start
      return openTime < slot.end && closeTime > slot.start;
    } else {
      // Period extends past midnight - it covers from openTime to 2359 today
      // Overlap if: openTime < slot.end
      return openTime < slot.end;
    }
  } else {
    // Overnight slot (lateNight: 2100-200)
    // For late night, we check if opening time is during the late night window
    // Based on the rule: "date is based on opening time"
    return openTime >= slot.start;
  }
}

/**
 * Check if a place is open during ANY of the specified day+slot combinations
 *
 * @param {Object} openingHours - The opening_hours object
 * @param {Array<number>} days - Array of days (0-6)
 * @param {Array<string>} slots - Array of slot keys ('morning', 'afternoon', etc.)
 * @returns {boolean|null} - true if open during ANY combination, false if closed all, null if unknown
 */
export const isOpenDuringAny = (openingHours, days, slots) => {
  if (!openingHours?.periods) {
    return null;
  }

  if (!days.length || !slots.length) {
    return null;
  }

  for (const day of days) {
    for (const slot of slots) {
      if (isOpenDuring(openingHours, day, slot) === true) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Check if a place is open during a specific hour range on a given day.
 * Used by the trip planner suggestion feature to filter places by time slot.
 *
 * @param {Object} openingHours - The opening_hours object from Google Places API
 * @param {number} dayOfWeek - Day of week (0=Sunday, 6=Saturday)
 * @param {number} startHour - Start of time range (0-23)
 * @param {number} endHour - End of time range (0-23)
 * @returns {boolean} - True if open during any part of the time range
 */
export const isOpenDuringTimeRange = (openingHours, dayOfWeek, startHour, endHour) => {
  // If no opening hours data, assume open (don't exclude from suggestions)
  if (!openingHours?.periods) {
    return true;
  }

  // Check for 24/7 places
  if (openingHours.periods.length === 1) {
    const period = openingHours.periods[0];
    if (!period.close && period.open?.day === 0 && getTimeValueFromPeriod(period.open) === 0) {
      return true; // Open 24/7
    }
  }

  // Convert hour range to HHMM format for comparison
  const rangeStart = startHour * 100;
  const rangeEnd = endHour * 100;

  // Find periods that are open on this day
  const dayPeriods = openingHours.periods.filter(p => p.open?.day === dayOfWeek);

  // Check if any period overlaps with our time range
  for (const period of dayPeriods) {
    const openTime = getTimeValueFromPeriod(period.open);

    // Handle 24-hour places (no close time)
    if (!period.close) {
      // If it opens during our range, it's open
      if (openTime >= rangeStart && openTime < rangeEnd) {
        return true;
      }
      // If it opens before our range and has no close, it's open during our range
      if (openTime < rangeStart) {
        return true;
      }
      continue;
    }

    const closeDay = period.close.day;
    const closeTime = getTimeValueFromPeriod(period.close);

    if (closeDay === dayOfWeek) {
      // Same day close - check if period overlaps with our range
      // Overlap exists if: openTime < rangeEnd AND closeTime > rangeStart
      if (openTime < rangeEnd && closeTime > rangeStart) {
        return true;
      }
    } else {
      // Closes next day (overnight hours, e.g., bar open until 2am)
      // If it opens on our day, it covers from openTime to midnight
      // Overlap exists if: openTime < rangeEnd
      if (openTime < rangeEnd) {
        return true;
      }
    }
  }

  // Also check if a period from yesterday extends into today's time range
  const yesterdayDay = (dayOfWeek + 6) % 7;
  const yesterdayPeriods = openingHours.periods.filter(p => p.open?.day === yesterdayDay);

  for (const period of yesterdayPeriods) {
    if (period.close?.day === dayOfWeek) {
      const closeTime = getTimeValueFromPeriod(period.close);
      // This place was open from yesterday and closes today
      // Overlap exists if: closeTime > rangeStart
      if (closeTime > rangeStart) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Helper to extract time value from a period's open/close object.
 * @private
 */
function getTimeValueFromPeriod(timeObj) {
  if (!timeObj) return 0;
  if (timeObj.hours !== undefined) {
    return timeObj.hours * 100 + (timeObj.minutes || 0);
  }
  if (timeObj.time !== undefined) {
    return parseInt(timeObj.time, 10) || 0;
  }
  return 0;
}

export default {
  isOpeningHoursStale,
  isPhotoUrlStale,
  calculateIsOpenNow,
  getOpenStatusInfo,
  isOpenDuring,
  isOpenDuringAny,
  isOpenDuringTimeRange,
  TIME_SLOTS,
  DAY_LABELS,
  DAY_LABELS_FULL,
};
